import { lazy as originLazy } from 'react';
import { Resource } from 'constants/global';
import { promiseRetry } from './router.util';

function lazy(fn) {
  return originLazy(() => promiseRetry(fn) as any);
}

export interface IRouterConfig {
  path: string;
  name: string;
  exact?: boolean;
  component?: any;
  redirect?: string;
  authority?: number[];
  routes?: IRouterConfig[];
}

// 这里的组件都采用懒加载是为了不让被依赖到的代码在多语言初始化前执行
const routerConfig: IRouterConfig[] = [
  // user
  {
    path: '/404',
    name: 'exception404',
    component: lazy(() => import('pages/Exception/Exception404')),
  },
  {
    path: '/403',
    name: 'exception403',
    component: lazy(() => import('pages/Exception/Exception403')),
  },
  {
    path: '/500',
    name: 'exception500',
    component: lazy(() => import('pages/Exception/Exception500')),
  },
  {
    path: '/login',
    name: 'login',
    component: lazy(() => import('pages/Login/index')),
  },
  {
    path: '/entrance',
    name: 'entrance',
    component: lazy(() => import('pages/Entrance')),
  },
  {
    path: '/bigoauth/appsflyer',
    name: 'oauth',
    component: lazy(() => import('pages/Oauth')),
  },
  {
    path: '/oauth/authorize',
    name: 'oauthCommon',
    component: lazy(() => import('pages/Oauth')),
  },
  {
    path: '/bigoauth/oauth2',
    name: 'oauth2',
    component: lazy(() => import('pages/Oauth2')),
  },
  // 即时表单预览页
  {
    path: '/resource/form/preview',
    name: 'resourceFormPreview',
    component: lazy(() => import('pages/Resource/Form/Preview')),
  },
  // home
  {
    path: '/',
    name: 'root',
    component: lazy(() => import('pages/HomeLayout')),
    routes: [
      {
        path: '/',
        name: 'rootHome',
        exact: true,
        component: lazy(() => import('pages/Homepage')),
      },

      {
        path: '/finance',
        name: 'finance',
        exact: true,
        component: lazy(() => import('pages/Finance')),
      },

      /* 投放管理 */
      {
        path: '/advert',
        name: 'advert',
        authority: [Resource.AdvertRead],
        component: lazy(() => import('pages/Advert')),
        routes: [
          {
            path: '/advert',
            exact: true,
            name: 'advertList',
            authority: [Resource.AdvertRead],
            component: lazy(() => import('pages/Advert/List')),
          },
          {
            path: '/advert/create',
            exact: true,
            name: 'advertCreate',
            authority: [Resource.AdvertEdit],
            component: lazy(() => import('pages/Advert/Create')),
          },
          {
            path: '/advert/copy',
            exact: true,
            name: 'advertCopy',
            authority: [Resource.AdvertEdit],
            component: lazy(() => import('pages/Advert/Copy')),
          },
          // 复制程序化创意，长得和新增表单差不多
          {
            path: '/advert/proceduralCopy',
            exact: true,
            name: 'proceduralCopy',
            authority: [Resource.AdvertEdit],
            component: lazy(() => import('pages/Advert/Create/ProceduralCopy')),
          },
          {
            path: '*',
            name: '404',
            component: lazy(() => import('pages/Exception/Exception404')),
          },
        ],
      },
      {
        path: '/report',
        name: 'report',
        authority: [Resource.ReportRead],
        component: lazy(() => import('pages/Report')),
        routes: [
          {
            path: '/report/static',
            exact: true,
            name: 'normalReport',
            authority: [Resource.ReportRead],
            component: lazy(() => import('pages/Report/Normal')),
          },
          {
            path: '/report/custom',
            exact: true,
            name: 'customReport',
            authority: [Resource.ReportRead],
            component: lazy(() => import('pages/Report/Custom')),
          },
          {
            path: '/report/home',
            exact: true,
            name: 'reportHome',
            authority: [Resource.ReportRead],
            component: lazy(() => import('pages/Report/Home')),
          },
          {
            path: '*',
            name: '404',
            component: lazy(() => import('pages/Exception/Exception404')),
          },
        ],
      },
      /* 资源库 */
      {
        path: '/resource',
        name: 'resource',
        component: lazy(() => import('pages/Resource')),
        routes: [
          /* 应用管理 */
          {
            path: '/resource/app',
            name: 'resourceApp',
            authority: [Resource.AppRead],
            component: lazy(() => import('pages/Resource/App')),
            routes: [
              {
                path: '/resource/app/add',
                exact: true,
                name: 'resourceAppAdd',
                authority: [Resource.AdvertEdit],
                component: lazy(() => import('pages/Resource/App/Add')),
              },
              {
                path: '/resource/app/detail',
                exact: true,
                name: 'resourceAppDetail',
                authority: [Resource.AppRead],
                component: lazy(() => import('pages/Resource/App/Detail')),
              },
              {
                path: '/resource/app',
                exact: true,
                name: 'resourceAppManage',
                authority: [Resource.AppRead],
                component: lazy(() => import('pages/Resource/App/Manage')),
                routes: [
                  {
                    path: '/resource/app',
                    exact: true,
                    name: 'resourceAppEvent',
                    authority: [Resource.AppRead],
                    component: lazy(() => import('pages/Resource/App/Manage/Event')),
                  },
                ],
              },
            ],
          },
          /* 网页事件 */
          {
            path: '/resource/webpage',
            name: 'resourceWebpage',
            authority: [Resource.WebPageRead],
            component: lazy(() => import('pages/Resource/Webpage')),
            routes: [
              {
                path: '/resource/webpage',
                exact: true,
                name: 'resourceWebpageList',
                component: lazy(() => import('pages/Resource/Webpage/List')),
              },
              {
                path: '/resource/webpage/add',
                exact: true,
                name: 'resourceWebpageAdd',
                authority: [Resource.WebPageEdit],
                component: lazy(() => import('pages/Resource/Webpage/Add')),
              },
              {
                path: '/resource/webpage/detail',
                exact: true,
                name: 'resourceWebpageDetail',
                component: lazy(() => import('pages/Resource/Webpage/Detail')),
              },
            ],
          },

          /* 网站Pixel */
          {
            path: '/resource/pixel',
            name: 'resourcePixel',
            authority: [Resource.Pixel],
            component: lazy(() => import('pages/Resource/Pixel')),
            routes: [
              {
                path: '/resource/pixel',
                exact: true,
                name: 'resourcePixelList',
                component: lazy(() => import('pages/Resource/Pixel/List')),
              },
              {
                path: '/resource/pixel/handler',
                exact: true,
                name: 'resourcePixelHandler',
                component: lazy(() => import('pages/Resource/Pixel/Handler')),
              },
              {
                path: '/resource/pixel/detail',
                exact: true,
                name: 'resourcePixelDetail',
                component: lazy(() => import('pages/Resource/Pixel/Detail')),
              },
              {
                path: '*',
                name: '404',
                component: lazy(() => import('pages/Exception/Exception404')),
              },
            ],
          },

          // 创意素材
          {
            path: '/resource/material',
            name: 'resourceMaterial',
            authority: [Resource.Material, Resource.MaterialRead],
            component: lazy(() => import('pages/Resource/Material')),
            routes: [
              {
                path: '/resource/material/video',
                exact: true,
                name: 'resourceMaterialVideo',
                component: lazy(() => import('pages/Resource/Material/Video')),
              },
              {
                path: '/resource/material/cover',
                exact: true,
                name: 'resourceMaterialCover',
                component: lazy(() => import('pages/Resource/Material/Cover')),
              },
              {
                path: '/resource/material/endcard',
                exact: true,
                name: 'resourceMaterialEndCard',
                component: lazy(() => import('pages/Resource/Material/EndCard')),
              },
              {
                path: '/resource/material/logo',
                exact: true,
                name: 'resourceMaterialLogo',
                component: lazy(() => import('pages/Resource/Material/Logo')),
              },
              {
                path: '/resource/material/playable',
                exact: true,
                name: 'resourceMaterialPlayable',
                component: lazy(() => import('pages/Resource/Material/Playable')),
              },
              {
                path: '/resource/material',
                name: 'resourceMaterialImage',
                component: lazy(() => import('pages/Resource/Material/Image')),
              },
            ],
          },

          /* 受众管理 */
          {
            path: '/resource/target',
            name: 'resourceTarget',
            authority: [Resource.Target, Resource.TargetRead],
            component: lazy(() => import('pages/Resource/Target')),
            routes: [
              {
                path: '/resource/target',
                exact: true,
                name: 'resourceTargetManage',
                component: lazy(() => import('pages/Resource/Target/List')),
              },
              {
                path: '/resource/target/add',
                exact: true,
                name: 'resourceTargetAdd',
                component: lazy(() => import('pages/Resource/Target/Add')),
              },
              {
                path: '/resource/target/detail',
                exact: true,
                name: 'resourceTargetDetail',
                component: lazy(() => import('pages/Resource/Target/Detail')),
              },
              {
                path: '*',
                name: '404',
                // eslint-disable-next-line global-require
                component: require('pages/Exception/Exception404').default,
              },
            ],
          },

          /* 商品管理 */
          {
            path: '/resource/goods',
            name: 'resourceGoods',
            authority: [Resource.Goods, Resource.GoodsRead],
            component: lazy(() => import('pages/Resource/Goods')),
            routes: [
              {
                path: '/resource/goods',
                exact: true,
                name: 'resourceGoodsDir',
                component: lazy(() => import('pages/Resource/Goods/IndexList')),
              },
              {
                path: '*',
                name: '404',
                // eslint-disable-next-line global-require
                component: require('pages/Exception/Exception404').default,
              },
            ],
          },
          /** 即时表单 */
          {
            path: '/resource/form',
            name: 'resourceForm',
            authority: [Resource.Form, Resource.FormRead],
            component: lazy(() => import('pages/Resource/Form')),
            routes: [
              {
                path: '/resource/form',
                exact: true,
                name: 'resourceFormList',
                component: lazy(() => import('pages/Resource/Form/List')),
              },
              {
                path: '*',
                name: '404',
                // eslint-disable-next-line global-require
                component: require('pages/Exception/Exception404').default,
              },
            ],
          },
        ],
      },
      {
        path: '/tools',
        name: 'Tools',
        authority: [Resource.Tools],
        component: lazy(() => import('pages/Entrance')),
        routes: [
          {
            path: '/tools/playableTransfer',
            name: 'PlayableTransfer',
            exact: true,
            component: lazy(() => import('pages/Entrance')),
          },
        ],
      },
      /* 消息管理 */
      {
        path: '/message',
        name: 'message',
        component: lazy(() => import('pages/Message')),
        routes: [
          {
            path: '/message/info',
            exact: true,
            name: 'messageInfo',
            component: lazy(() => import('pages/Message/Info')),
          },
          {
            path: '/message/setting',
            exact: true,
            name: 'messageSetting',
            component: lazy(() => import('pages/Message/Setting')),
          },
          {
            path: '*',
            name: '404',
            component: lazy(() => import('pages/Exception/Exception404')),
          },
        ],
      },

      {
        path: '/account',
        name: 'account',
        authority: [Resource.AccountRead],
        component: lazy(() => import('pages/Account')),
      },
      {
        path: '/subscribe',
        name: 'subscribe',
        component: lazy(() => import('pages/Subscribe')),
      },
      {
        path: '*',
        name: '404',
        component: lazy(() => import('pages/Exception/Exception404')),
      },
    ],
  },
];

export default routerConfig;
